export const CHECKOUT_ADJUSTMENT_DRAFT_KEY =
  'qv_checkout_adjustments_v1';
export const CHECKOUT_ADJUSTMENT_TTL_MS = 60 * 60 * 1000;

const DRAFT_VERSION = 2;
const MAX_IDENTIFIER_LENGTH = 256;
const MAX_COUPON_LENGTH = 128;
const MAX_AMOUNT = 1_000_000_000;
const ALLOWED_ORDER_METHODS = new Set(['pickup', 'delivery']);
const ALLOWED_TIP_PERCENTAGES = new Set([10, 15, 20, 25]);
const DRAFT_KEYS = [
  'version',
  'merchantId',
  'orderMethod',
  'sessionScope',
  'updatedAt',
  'expiresAt',
  'couponCode',
  'loyaltyAmount',
  'storeCreditAmount',
  'tip',
  'tipTouched',
];
const PATCH_KEYS = new Set([
  'couponCode',
  'loyaltyAmount',
  'storeCreditAmount',
  'tip',
]);
const DECIMAL_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
const MERCHANT_ROUTE_PATTERNS = [
  /^\/(?:merchant|product|payment-cart|wishlist|popularproduct|loyalty-points|buy-it-again)\/([^/]+)\/?$/i,
  /^\/old\/merchant\/([^/]+)\/?$/i,
  /^\/([^/]+)\/category\/[^/]+\/[^/]+\/?$/i,
];

const hasOnlySafeText = (value) =>
  [...value].every((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint >= 32 && codePoint !== 127;
  });

const isPlainObject = (value) =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const hasExactKeys = (value, expectedKeys) => {
  const actualKeys = Object.keys(value).sort();
  const sortedExpectedKeys = [...expectedKeys].sort();

  return (
    actualKeys.length === sortedExpectedKeys.length &&
    actualKeys.every((key, index) => key === sortedExpectedKeys[index])
  );
};

const sanitizeText = (value, maxLength) => {
  if (typeof value !== 'string') return null;

  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > maxLength ||
    !hasOnlySafeText(normalized)
  ) {
    return null;
  }

  return normalized;
};

const sanitizeAmount = (value, { allowZero = true } = {}) => {
  if (value === null || value === '') return allowZero ? 0 : null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;

  const normalized = typeof value === 'string' ? value.trim() : value;
  if (
    normalized === '' ||
    (typeof normalized === 'string' && !DECIMAL_PATTERN.test(normalized))
  ) {
    return null;
  }

  const amount = Number(normalized);
  if (
    !Number.isFinite(amount) ||
    amount < 0 ||
    amount > MAX_AMOUNT ||
    (!allowZero && amount === 0)
  ) {
    return null;
  }

  return amount;
};

const sanitizeCouponCode = (value) => {
  if (value === null || value === '') return '';
  return sanitizeText(value, MAX_COUPON_LENGTH);
};

const sanitizeTip = (value) => {
  if (value === null) return { mode: 'none', value: 0 };
  if (!isPlainObject(value) || !hasExactKeys(value, ['mode', 'value'])) {
    return null;
  }

  if (value.mode === 'none') {
    const amount = sanitizeAmount(value.value);
    return amount === 0 ? { mode: 'none', value: 0 } : null;
  }

  if (value.mode !== 'percent' && value.mode !== 'custom') return null;

  const amount = sanitizeAmount(value.value, { allowZero: false });
  if (
    amount === null ||
    (value.mode === 'percent' && !ALLOWED_TIP_PERCENTAGES.has(amount))
  ) {
    return null;
  }

  return { mode: value.mode, value: amount };
};

const sanitizeAdjustments = (value) => {
  const couponCode = sanitizeCouponCode(value.couponCode);
  const loyaltyAmount = sanitizeAmount(value.loyaltyAmount);
  const storeCreditAmount = sanitizeAmount(value.storeCreditAmount);
  const tip = sanitizeTip(value.tip);
  const tipTouched =
    typeof value.tipTouched === 'boolean' ? value.tipTouched : null;

  if (
    couponCode === null ||
    loyaltyAmount === null ||
    storeCreditAmount === null ||
    tip === null ||
    tipTouched === null ||
    (tipTouched === false && tip.mode !== 'none')
  ) {
    return null;
  }

  return {
    couponCode,
    loyaltyAmount,
    storeCreditAmount,
    tip,
    tipTouched,
  };
};

const adjustmentsMatch = (draft, adjustments) =>
  Boolean(draft) &&
  draft.couponCode === adjustments.couponCode &&
  draft.loyaltyAmount === adjustments.loyaltyAmount &&
  draft.storeCreditAmount === adjustments.storeCreditAmount &&
  draft.tip.mode === adjustments.tip.mode &&
  draft.tip.value === adjustments.tip.value &&
  draft.tipTouched === adjustments.tipTouched;

export const isCheckoutTipExplicitlyRemoved = (draft) =>
  draft?.tipTouched === true && draft?.tip?.mode === 'none';

const getBrowserStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getCheckoutMerchantIdFromPath = (pathname) => {
  if (typeof pathname !== 'string') return null;

  for (const pattern of MERCHANT_ROUTE_PATTERNS) {
    const match = pathname.match(pattern);
    if (!match?.[1]) continue;

    return sanitizeText(safeDecode(match[1]), MAX_IDENTIFIER_LENGTH);
  }

  return null;
};

const readCookie = (name) => {
  try {
    if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
      return null;
    }

    const prefix = `${encodeURIComponent(name)}=`;
    const pair = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    return pair ? safeDecode(pair.slice(prefix.length)) : null;
  } catch {
    return null;
  }
};

const parseStoredObject = (value) => {
  if (isPlainObject(value)) return value;
  if (typeof value !== 'string' || value.trim() === '') return null;

  try {
    const parsed = JSON.parse(value);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const readBrowserValue = (key) => {
  const cookieValue = readCookie(key);
  if (cookieValue !== null) return cookieValue;

  try {
    return getBrowserStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (['1', 'true', 'yes'].includes(normalized)) return true;
  if (['0', 'false', 'no'].includes(normalized)) return false;
  return null;
};

const findIdentity = (value) => {
  if (!isPlainObject(value)) return null;

  const nestedUser = isPlainObject(value.UserData) ? value.UserData : null;
  const source = nestedUser || value;
  const candidates = [
    source.id,
    source.customer_id,
    source.customerId,
    source.user_id,
    source.userId,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return String(candidate);
    }

    const normalized = sanitizeText(candidate, MAX_IDENTIFIER_LENGTH);
    if (normalized) return normalized;
  }

  return null;
};

const fingerprint = (value) => {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(36);
};

const findFallbackIdentity = (value) => {
  if (!isPlainObject(value)) return null;

  const source = isPlainObject(value.UserData) ? value.UserData : value;
  const candidate =
    source.email ||
    source.username ||
    source.phone ||
    source.mobile ||
    source.token;

  if (typeof candidate !== 'string' || candidate.trim() === '') return null;
  return `fallback-${fingerprint(candidate.trim())}`;
};

const findSessionToken = (options, userData) => {
  const candidates = [
    options.loginToken,
    options.login_token,
    options.token,
    userData?.loginToken,
    userData?.login_token,
    userData?.token,
    readBrowserValue('login_token'),
  ];

  const token = candidates.find(
    (candidate) =>
      typeof candidate === 'string' && candidate.trim().length > 0,
  );

  return token ? fingerprint(token.trim()) : null;
};

/**
 * Returns a stable, non-secret scope for the active customer or guest session.
 */
export const getCheckoutSessionScope = (options = {}) => {
  const normalizedOptions = isPlainObject(options) ? options : {};
  const explicitUserData = isPlainObject(normalizedOptions.userData)
    ? normalizedOptions.userData
    : isPlainObject(normalizedOptions.UserData)
      ? normalizedOptions.UserData
      : null;
  const storedUserData = parseStoredObject(readBrowserValue('userLoginData'));
  const explicitIdentity =
    findIdentity(explicitUserData) || findFallbackIdentity(explicitUserData);
  const storedIdentity =
    findIdentity(storedUserData) || findFallbackIdentity(storedUserData);
  const userData = explicitIdentity ? explicitUserData : storedUserData;
  const storedGuest = normalizeBoolean(readBrowserValue('is_guest'));
  const explicitGuest = normalizeBoolean(
    normalizedOptions.isGuest ??
      normalizedOptions.is_guest ??
      userData?.isGuest ??
      userData?.is_guest,
  );
  const isGuest = explicitGuest ?? storedGuest ?? false;
  const explicitAuthenticated = normalizeBoolean(
    normalizedOptions.isAuthenticated,
  );
  const identity = explicitIdentity || storedIdentity;
  const sessionToken = findSessionToken(normalizedOptions, userData);
  const tokenSuffix = sessionToken ? `:session-${sessionToken}` : '';

  if (isGuest) return `guest:${identity || 'anonymous'}${tokenSuffix}`;
  if (identity) return `user:${identity}${tokenSuffix}`;
  if (explicitAuthenticated) return `user:anonymous${tokenSuffix}`;
  if (explicitAuthenticated === false) {
    return `guest:${identity || 'anonymous'}${tokenSuffix}`;
  }
  return 'anonymous';
};

export const createCheckoutDraftScope = (scope = {}) => {
  if (!isPlainObject(scope)) return null;

  const { merchantId, orderMethod, sessionScope } = scope;
  const normalizedMerchantId = sanitizeText(
    merchantId,
    MAX_IDENTIFIER_LENGTH,
  );
  const normalizedOrderMethod =
    typeof orderMethod === 'string' ? orderMethod.trim().toLowerCase() : '';
  const normalizedSessionScope = sanitizeText(
    sessionScope,
    MAX_IDENTIFIER_LENGTH,
  );

  if (
    !normalizedMerchantId ||
    !ALLOWED_ORDER_METHODS.has(normalizedOrderMethod) ||
    !normalizedSessionScope
  ) {
    return null;
  }

  return {
    merchantId: normalizedMerchantId.toUpperCase(),
    orderMethod: normalizedOrderMethod,
    sessionScope: normalizedSessionScope,
  };
};

const scopesMatch = (draft, scope) =>
  draft.merchantId === scope.merchantId &&
  draft.orderMethod === scope.orderMethod &&
  draft.sessionScope === scope.sessionScope;

const sanitizeStoredDraft = (value, now) => {
  if (!isPlainObject(value) || !hasExactKeys(value, DRAFT_KEYS)) return null;
  if (
    value.version !== DRAFT_VERSION ||
    !Number.isSafeInteger(value.updatedAt) ||
    !Number.isSafeInteger(value.expiresAt) ||
    value.updatedAt < 0 ||
    value.expiresAt !== value.updatedAt + CHECKOUT_ADJUSTMENT_TTL_MS ||
    value.expiresAt <= now
  ) {
    return null;
  }

  const scope = createCheckoutDraftScope(value);
  const adjustments = sanitizeAdjustments(value);
  if (!scope || !adjustments) return null;

  return {
    version: DRAFT_VERSION,
    ...scope,
    updatedAt: value.updatedAt,
    expiresAt: value.expiresAt,
    ...adjustments,
  };
};

export const clearCheckoutAdjustmentDraft = () => {
  const storage = getBrowserStorage();
  if (!storage) return false;

  try {
    storage.removeItem(CHECKOUT_ADJUSTMENT_DRAFT_KEY);
    return true;
  } catch {
    return false;
  }
};

export const clearCheckoutAdjustmentDraftForMerchantChange = (merchantId) => {
  const normalizedMerchantId = sanitizeText(
    merchantId,
    MAX_IDENTIFIER_LENGTH,
  );
  const storage = getBrowserStorage();
  if (!normalizedMerchantId || !storage) return false;

  try {
    const serialized = storage.getItem(CHECKOUT_ADJUSTMENT_DRAFT_KEY);
    if (serialized === null) return false;

    let parsed;
    try {
      parsed = JSON.parse(serialized);
    } catch {
      return clearCheckoutAdjustmentDraft();
    }

    const draft = sanitizeStoredDraft(parsed, Date.now());
    if (
      !draft ||
      draft.merchantId !== normalizedMerchantId.toUpperCase()
    ) {
      return clearCheckoutAdjustmentDraft();
    }

    return false;
  } catch {
    return false;
  }
};

export const readCheckoutAdjustmentDraft = (scope) => {
  const normalizedScope = createCheckoutDraftScope(scope);
  const storage = getBrowserStorage();
  if (!normalizedScope || !storage) return null;

  try {
    const serialized = storage.getItem(CHECKOUT_ADJUSTMENT_DRAFT_KEY);
    if (serialized === null) return null;

    let parsed;
    try {
      parsed = JSON.parse(serialized);
    } catch {
      clearCheckoutAdjustmentDraft();
      return null;
    }

    const draft = sanitizeStoredDraft(parsed, Date.now());
    if (!draft || !scopesMatch(draft, normalizedScope)) {
      clearCheckoutAdjustmentDraft();
      return null;
    }

    return draft;
  } catch {
    return null;
  }
};

export const patchCheckoutAdjustmentDraft = (
  scope,
  patch,
  { refreshExpiry = true } = {},
) => {
  const normalizedScope = createCheckoutDraftScope(scope);
  const storage = getBrowserStorage();
  if (!normalizedScope || !storage || !isPlainObject(patch)) return null;

  const patchKeys = Object.keys(patch);
  if (
    patchKeys.length === 0 ||
    patchKeys.some(
      (key) => !PATCH_KEYS.has(key) || patch[key] === undefined,
    )
  ) {
    return null;
  }

  const existing = readCheckoutAdjustmentDraft(normalizedScope);
  if (!existing && refreshExpiry === false) return null;

  const currentAdjustments = existing
    ? {
        couponCode: existing.couponCode,
        loyaltyAmount: existing.loyaltyAmount,
        storeCreditAmount: existing.storeCreditAmount,
        tip: existing.tip,
        tipTouched: existing.tipTouched,
      }
    : {
        couponCode: '',
        loyaltyAmount: 0,
        storeCreditAmount: 0,
        tip: { mode: 'none', value: 0 },
        tipTouched: false,
      };
  const nextAdjustments = { ...currentAdjustments };

  for (const key of patchKeys) nextAdjustments[key] = patch[key];
  if (Object.prototype.hasOwnProperty.call(patch, 'tip')) {
    nextAdjustments.tipTouched = true;
  }

  const sanitizedAdjustments = sanitizeAdjustments(nextAdjustments);
  if (!sanitizedAdjustments) return null;

  if (adjustmentsMatch(existing, sanitizedAdjustments)) return existing;

  const now = Date.now();
  const preserveExistingExpiry = existing && refreshExpiry === false;
  const updatedAt = preserveExistingExpiry ? existing.updatedAt : now;
  const draft = {
    version: DRAFT_VERSION,
    ...normalizedScope,
    updatedAt,
    expiresAt: preserveExistingExpiry
      ? existing.expiresAt
      : now + CHECKOUT_ADJUSTMENT_TTL_MS,
    ...sanitizedAdjustments,
  };

  try {
    storage.setItem(CHECKOUT_ADJUSTMENT_DRAFT_KEY, JSON.stringify(draft));
    return draft;
  } catch {
    return null;
  }
};
